import React from "react";

import SettingsIcon from "@uui/Settings04";

import {
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import styled from "@mui/material/styles/styled";

import { FloatingNavButton } from "@app/analytics/components/reports/FloatingNav";
import useTranscriptStore, {
  TextSize,
} from "@app/analytics/MeetingReport/stores/transcript";
import useVideoStore from "@app/analytics/MeetingReport/stores/video";
import useUserMetadata from "@app/common/auth/useUserMetadata";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import Menu, { MenuBody, MenuDivider, MenuHeader } from "@rui/Menu";
import { Row } from "@rui/Stack";
import Switch from "@rui/Switch";
import TooltipIcon from "@rui/TooltipIcon";
import TooltipWrapper from "@rui/TooltipWrapper";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  "& .MuiToggleButton-root": {
    borderRadius: "4px !important",
    fontWeight: 400,
    height: "28px",
    minHeight: "28px",
    padding: "2px 8px",
    color: theme.palette.shades.neutral[600],
    backgroundColor: theme.palette.shades.neutral[50],
    border: "none",
    fontFamily: theme.typography.fontFamilySecondary,

    "&:not(:first-of-type)": {
      marginLeft: theme.spacing(1),
    },

    "&.Mui-selected": {
      color: "#FFF",
      backgroundColor: theme.palette.overlay.primary[24],
      "&:hover": {
        color: "#FFF",
        backgroundColor: theme.palette.overlay.primary[24],
      },
    },
  },
}));

const SwitchLabel = styled(Typography)(({ theme }) => ({
  ...theme.typography.bodySecondaryReg,
  color: theme.palette.shades.neutral[800],
  whiteSpace: "nowrap",
}));

const RecapSettings = () => {
  const { anchorElement, onClick, open, onClose } = useAnchorElement();
  const { userMetadata, updateUserMetadata } = useUserMetadata();

  const { autoPlay, playbackEnabled, toggleAutoPlay } = useVideoStore(
    (state) => ({
      autoPlay: state.autoPlay,
      playbackEnabled: state.playbackEnabled,
      toggleAutoPlay: state.toggleAutoPlay,
    })
  );

  const { textSize, setTextSize, reactionsEnabled, toggleReactionsEnabled } =
    useTranscriptStore((state) => ({
      textSize: state.textSize,
      setTextSize: state.setTextSize,
      reactionsEnabled: state.reactionsEnabled,
      toggleReactionsEnabled: state.toggleReactionsEnabled,
    }));

  const handleTextSizeChange = (
    event: React.MouseEvent<HTMLElement>,
    size: string
  ) => {
    if (size) {
      setTextSize(size as TextSize);
    }
  };

  const toggleAutoPlayOnLoad = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    updateUserMetadata.mutate({ autoPlayMeetingReportVideoOnLoad: checked });
  };

  return (
    <>
      <TooltipWrapper placement="bottom" title="Settings">
        <FloatingNavButton Icon={SettingsIcon} onClick={onClick} active={open}>
          Settings
        </FloatingNavButton>
      </TooltipWrapper>
      <Menu
        open={open}
        onClose={onClose}
        anchorEl={anchorElement}
        disablePadding
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuHeader>
          <SettingsIcon />
          <span>Settings</span>
        </MenuHeader>
        <MenuBody spacing={0.5}>
          <FormControlLabel
            control={<Switch onChange={toggleAutoPlayOnLoad} />}
            checked={userMetadata?.autoPlayMeetingReportVideoOnLoad ?? true}
            disabled={!playbackEnabled}
            label={
              <Row variant="left" spacing={1}>
                <SwitchLabel>Auto-play video on load</SwitchLabel>
                <TooltipIcon
                  placement="bottom"
                  title="Video will automatically play on page load"
                />
              </Row>
            }
          />
          <FormControlLabel
            control={<Switch onChange={toggleAutoPlay} />}
            checked={playbackEnabled && autoPlay}
            disabled={!playbackEnabled}
            label={
              <Row variant="left" spacing={1}>
                <SwitchLabel>Auto-play video on click</SwitchLabel>
                <TooltipIcon
                  placement="bottom"
                  title="Video will automatically play when clicking on notes, transcript turns, and highlights"
                />
              </Row>
            }
          />
          <FormControlLabel
            control={<Switch onChange={toggleReactionsEnabled} />}
            checked={reactionsEnabled}
            label={<SwitchLabel>Highlight reactions in transcript</SwitchLabel>}
          />
          <MenuDivider />
          <Row variant="apart" spacing={3}>
            <Typography noWrap>Text size</Typography>
            <Row spacing={1}>
              <StyledToggleButtonGroup
                value={textSize}
                onChange={handleTextSizeChange}
                exclusive
              >
                <ToggleButton value="0.875em" sx={{ fontSize: "0.875em" }}>
                  small
                </ToggleButton>
                <ToggleButton value="1em" sx={{ fontSize: "1em" }}>
                  default
                </ToggleButton>
                <ToggleButton value="1.125em" sx={{ fontSize: "1.125em" }}>
                  large
                </ToggleButton>
              </StyledToggleButtonGroup>
            </Row>
          </Row>
        </MenuBody>
      </Menu>
    </>
  );
};

export default RecapSettings;
